'use client'

import * as React from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import { cn } from '../../../lib/utils'

dayjs.extend(utc)
dayjs.extend(timezone)

export interface TimeInputProps {
  value: Date | undefined
  onChange: (date: Date | undefined) => void
  use12Hour?: boolean
  timeZone?: string
  disabled?: boolean
  className?: string
  label?: string
}

type TimeSegment = 'hour' | 'minute' | 'meridiem'

const BUFFER_COMMIT_MS = 1000

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value))
}

function hourNeedsSecondDigit(digit: number, use12Hour: boolean) {
  return use12Hour ? digit <= 1 : digit <= 2
}

/**
 * Turn an input edit into the digits the user just entered.
 * A focused segment starts fully selected, so the first keystroke replaces it.
 * Further keystrokes append to the short pending buffer.
 */
function typedDigits(
  shown: string,
  raw: string,
  editingPending: boolean,
): string {
  const next = raw.replace(/\D/g, '')
  if (editingPending) return next.slice(0, 2)
  if (next.length <= 2) return next
  const previous = shown.replace(/\D/g, '')
  if (next.startsWith(previous))
    return next.slice(previous.length, previous.length + 1)
  if (next.endsWith(previous))
    return next.slice(0, next.length - previous.length).slice(-1)
  return next.slice(-1)
}

function TimeInput({
  value,
  onChange,
  use12Hour = true,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  disabled = false,
  className,
  label,
}: TimeInputProps) {
  const hourRef = React.useRef<HTMLInputElement>(null)
  const minuteRef = React.useRef<HTMLInputElement>(null)
  const meridiemRef = React.useRef<HTMLButtonElement>(null)

  const [focusedSegment, setFocusedSegment] =
    React.useState<TimeSegment | null>(null)
  const [hourBuffer, setHourBuffer] = React.useState('')
  const [minuteBuffer, setMinuteBuffer] = React.useState('')
  const hourBufferRef = React.useRef('')
  const minuteBufferRef = React.useRef('')
  const bufferTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  const clearBufferTimer = React.useCallback(() => {
    if (bufferTimeoutRef.current) {
      clearTimeout(bufferTimeoutRef.current)
      bufferTimeoutRef.current = null
    }
  }, [])

  React.useEffect(() => clearBufferTimer, [clearBufferTimer])

  const currentDate = React.useMemo(() => {
    if (!value) return dayjs().tz(timeZone)
    return dayjs(value).tz(timeZone)
  }, [value, timeZone])

  const hour24 = currentDate.hour()
  const minute = currentDate.minute()
  const meridiem = hour24 >= 12 ? 'PM' : 'AM'

  const displayHour = React.useMemo(() => {
    if (!use12Hour) return hour24
    if (hour24 === 0) return 12
    if (hour24 > 12) return hour24 - 12
    return hour24
  }, [hour24, use12Hour])

  const hourMin = use12Hour ? 1 : 0
  const hourMax = use12Hour ? 12 : 23

  const updateTime = React.useCallback(
    (newHour24: number, newMinute: number) => {
      const base = (value ? dayjs(value) : dayjs()).tz(timeZone)
      const updated = base
        .hour(newHour24)
        .minute(newMinute)
        .second(0)
        .millisecond(0)
      onChange(updated.toDate())
    },
    [value, onChange, timeZone],
  )

  const handleHourChange = React.useCallback(
    (newDisplayHour: number) => {
      let newHour24: number
      if (use12Hour) {
        if (meridiem === 'AM') {
          newHour24 = newDisplayHour === 12 ? 0 : newDisplayHour
        } else {
          newHour24 = newDisplayHour === 12 ? 12 : newDisplayHour + 12
        }
      } else {
        newHour24 = newDisplayHour
      }
      updateTime(newHour24, minute)
    },
    [use12Hour, meridiem, minute, updateTime],
  )

  const handleMinuteChange = React.useCallback(
    (newMinute: number) => {
      updateTime(hour24, newMinute)
    },
    [hour24, updateTime],
  )

  const handleMeridiemToggle = React.useCallback(() => {
    const newHour24 = meridiem === 'AM' ? hour24 + 12 : hour24 - 12
    updateTime(clamp(newHour24, 0, 23), minute)
  }, [meridiem, hour24, minute, updateTime])

  const setHourPending = React.useCallback((digits: string) => {
    hourBufferRef.current = digits
    setHourBuffer(digits)
  }, [])

  const setMinutePending = React.useCallback((digits: string) => {
    minuteBufferRef.current = digits
    setMinuteBuffer(digits)
  }, [])

  const focusSegment = React.useCallback(
    (segment: 'minute' | 'hour' | 'meridiem') => {
      const node =
        segment === 'hour'
          ? hourRef.current
          : segment === 'minute'
            ? minuteRef.current
            : meridiemRef.current
      node?.focus()
      if (node instanceof HTMLInputElement) node.select()
    },
    [],
  )

  const commitHour = React.useCallback(
    (rawHour: number, advance: boolean) => {
      clearBufferTimer()
      setHourPending('')
      handleHourChange(clamp(rawHour, hourMin, hourMax))
      if (advance) focusSegment('minute')
    },
    [
      clearBufferTimer,
      focusSegment,
      handleHourChange,
      hourMax,
      hourMin,
      setHourPending,
    ],
  )

  const commitMinute = React.useCallback(
    (rawMinute: number, advance: boolean) => {
      clearBufferTimer()
      setMinutePending('')
      handleMinuteChange(clamp(rawMinute, 0, 59))
      if (advance && use12Hour) focusSegment('meridiem')
    },
    [
      clearBufferTimer,
      focusSegment,
      handleMinuteChange,
      setMinutePending,
      use12Hour,
    ],
  )

  const commitHourRef = React.useRef(commitHour)
  const commitMinuteRef = React.useRef(commitMinute)
  React.useEffect(() => {
    commitHourRef.current = commitHour
    commitMinuteRef.current = commitMinute
  }, [commitHour, commitMinute])

  const scheduleBufferCommit = React.useCallback(
    (segment: 'hour' | 'minute') => {
      clearBufferTimer()
      bufferTimeoutRef.current = setTimeout(() => {
        if (segment === 'hour' && hourBufferRef.current) {
          const pending = parseInt(hourBufferRef.current, 10)
          const advance = document.activeElement === hourRef.current
          commitHourRef.current(pending, advance)
          return
        }
        if (segment === 'minute' && minuteBufferRef.current) {
          const pending = parseInt(minuteBufferRef.current, 10)
          const advance = document.activeElement === minuteRef.current
          commitMinuteRef.current(pending, advance)
        }
      }, BUFFER_COMMIT_MS)
    },
    [clearBufferTimer],
  )

  React.useEffect(() => {
    const node =
      focusedSegment === 'hour'
        ? hourRef.current
        : focusedSegment === 'minute'
          ? minuteRef.current
          : null
    const pending = focusedSegment === 'hour' ? hourBuffer : minuteBuffer
    if (!node || !pending) return
    const len = pending.length
    node.setSelectionRange(len, len)
  }, [focusedSegment, hourBuffer, minuteBuffer])

  const applyHourDigits = React.useCallback(
    (digits: string) => {
      if (!digits) {
        clearBufferTimer()
        setHourPending('')
        return
      }
      if (digits.length === 1) {
        const parsed = parseInt(digits, 10)
        if (hourNeedsSecondDigit(parsed, use12Hour)) {
          setHourPending(digits)
          scheduleBufferCommit('hour')
          return
        }
        commitHour(parsed, true)
        return
      }
      commitHour(parseInt(digits, 10), true)
    },
    [
      clearBufferTimer,
      commitHour,
      scheduleBufferCommit,
      setHourPending,
      use12Hour,
    ],
  )

  const applyMinuteDigits = React.useCallback(
    (digits: string) => {
      if (!digits) {
        clearBufferTimer()
        setMinutePending('')
        return
      }
      if (digits.length === 1) {
        const parsed = parseInt(digits, 10)
        if (parsed <= 5) {
          setMinutePending(digits)
          scheduleBufferCommit('minute')
          return
        }
        commitMinute(parsed, true)
        return
      }
      commitMinute(parseInt(digits, 10), true)
    },
    [clearBufferTimer, commitMinute, scheduleBufferCommit, setMinutePending],
  )

  const incrementHour = React.useCallback(
    (delta: number) => {
      clearBufferTimer()
      setHourPending('')
      let next = displayHour + delta
      if (next > hourMax) next = hourMin
      if (next < hourMin) next = hourMax
      handleHourChange(next)
    },
    [
      clearBufferTimer,
      displayHour,
      handleHourChange,
      hourMax,
      hourMin,
      setHourPending,
    ],
  )

  const incrementMinute = React.useCallback(
    (delta: number) => {
      clearBufferTimer()
      setMinutePending('')
      let next = minute + delta
      if (next >= 60) next = 0
      if (next < 0) next = 59
      handleMinuteChange(next)
    },
    [clearBufferTimer, handleMinuteChange, minute, setMinutePending],
  )

  const hourShown =
    focusedSegment === 'hour' && hourBuffer !== ''
      ? hourBuffer
      : displayHour.toString().padStart(2, '0')
  const minuteShown =
    focusedSegment === 'minute' && minuteBuffer !== ''
      ? minuteBuffer
      : minute.toString().padStart(2, '0')

  const handleHourKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault()
        incrementHour(1)
        break
      case 'ArrowDown':
        e.preventDefault()
        incrementHour(-1)
        break
      case 'ArrowRight':
        e.preventDefault()
        focusSegment('minute')
        break
      case 'ArrowLeft':
        break
      default:
        break
    }
  }

  const handleMinuteKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault()
        incrementMinute(1)
        break
      case 'ArrowDown':
        e.preventDefault()
        incrementMinute(-1)
        break
      case 'ArrowRight':
        if (use12Hour) {
          e.preventDefault()
          focusSegment('meridiem')
        }
        break
      case 'ArrowLeft':
        e.preventDefault()
        focusSegment('hour')
        break
      case 'Backspace':
        if (minuteBufferRef.current === '') {
          e.preventDefault()
          focusSegment('hour')
        }
        break
      default:
        break
    }
  }

  const handleMeridiemKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return
    switch (e.key) {
      case 'ArrowUp':
      case 'ArrowDown':
      case ' ':
      case 'Enter':
        e.preventDefault()
        handleMeridiemToggle()
        break
      case 'ArrowLeft':
      case 'Backspace':
        e.preventDefault()
        focusSegment('minute')
        break
      case 'a':
      case 'A':
        e.preventDefault()
        if (meridiem === 'PM') handleMeridiemToggle()
        break
      case 'p':
      case 'P':
        e.preventDefault()
        if (meridiem === 'AM') handleMeridiemToggle()
        break
      case 'Tab':
        if (e.shiftKey) {
          e.preventDefault()
          focusSegment('minute')
        }
        break
      default:
        break
    }
  }

  const nativeValue = currentDate.format('HH:mm')

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label ? (
        <span className="hidden text-sm text-muted-foreground md:inline">
          {label}
        </span>
      ) : null}
      <label className="flex w-full flex-col gap-1.5 md:hidden">
        <span className="text-sm text-muted-foreground">{label ?? 'Time'}</span>
        <input
          type="time"
          aria-label={label ?? 'Time'}
          value={nativeValue}
          disabled={disabled}
          onChange={(e) => {
            const match = /^(\d{2}):(\d{2})/.exec(e.target.value)
            if (!match) return
            updateTime(Number(match[1]), Number(match[2]))
          }}
          className={cn(
            'min-h-11 w-full rounded-md border border-input bg-transparent px-3 text-base text-foreground',
            "[[data-theme='dark']_&]:[color-scheme:dark]",
            'focus:outline-none focus:ring-1 focus:ring-ring',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        />
      </label>

      <div
        className="hidden items-center gap-1 md:flex md:self-center"
        role="group"
        aria-label={label ?? 'Time'}
      >
        <div
          className={cn(
            'flex h-9 items-center rounded-md border border-input bg-transparent px-2',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          <input
            ref={hourRef}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            role="spinbutton"
            aria-label="Hour"
            aria-valuenow={
              focusedSegment === 'hour' && hourBuffer
                ? parseInt(hourBuffer, 10)
                : displayHour
            }
            aria-valuemin={hourMin}
            aria-valuemax={hourMax}
            aria-valuetext={hourShown}
            value={hourShown}
            disabled={disabled}
            onFocus={() => {
              setFocusedSegment('hour')
              hourRef.current?.select()
            }}
            onBlur={() => {
              setFocusedSegment((current) =>
                current === 'hour' ? null : current,
              )
              const pending = hourBufferRef.current
              if (!pending) return
              commitHour(parseInt(pending, 10), false)
            }}
            onChange={(e) => {
              if (disabled) return
              applyHourDigits(
                typedDigits(
                  hourShown,
                  e.target.value,
                  hourBufferRef.current !== '',
                ),
              )
            }}
            onKeyDown={handleHourKeyDown}
            className={cn(
              'w-8 bg-transparent text-center text-sm tabular-nums outline-none',
              'rounded-sm focus:bg-accent focus:ring-1 focus:ring-ring',
              'selection:bg-primary selection:text-primary-foreground',
              disabled && 'cursor-not-allowed',
            )}
          />
          <span className="text-muted-foreground" aria-hidden>
            :
          </span>
          <input
            ref={minuteRef}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            role="spinbutton"
            aria-label="Minute"
            aria-valuenow={
              focusedSegment === 'minute' && minuteBuffer
                ? parseInt(minuteBuffer, 10)
                : minute
            }
            aria-valuemin={0}
            aria-valuemax={59}
            aria-valuetext={minuteShown}
            value={minuteShown}
            disabled={disabled}
            onFocus={() => {
              setFocusedSegment('minute')
              minuteRef.current?.select()
            }}
            onBlur={() => {
              setFocusedSegment((current) =>
                current === 'minute' ? null : current,
              )
              const pending = minuteBufferRef.current
              if (!pending) return
              commitMinute(parseInt(pending, 10), false)
            }}
            onChange={(e) => {
              if (disabled) return
              applyMinuteDigits(
                typedDigits(
                  minuteShown,
                  e.target.value,
                  minuteBufferRef.current !== '',
                ),
              )
            }}
            onKeyDown={handleMinuteKeyDown}
            className={cn(
              'w-8 bg-transparent text-center text-sm tabular-nums outline-none',
              'rounded-sm focus:bg-accent focus:ring-1 focus:ring-ring',
              'selection:bg-primary selection:text-primary-foreground',
              disabled && 'cursor-not-allowed',
            )}
          />
        </div>

        {use12Hour ? (
          <button
            ref={meridiemRef}
            type="button"
            role="spinbutton"
            disabled={disabled}
            aria-label={`Meridiem, currently ${meridiem}`}
            aria-valuetext={meridiem}
            aria-valuemin={0}
            aria-valuemax={1}
            aria-valuenow={meridiem === 'AM' ? 0 : 1}
            onClick={handleMeridiemToggle}
            onFocus={() => setFocusedSegment('meridiem')}
            onBlur={() =>
              setFocusedSegment((current) =>
                current === 'meridiem' ? null : current,
              )
            }
            onKeyDown={handleMeridiemKeyDown}
            className={cn(
              'flex h-9 min-w-11 items-center justify-center rounded-md border border-input bg-transparent px-2 text-sm font-medium',
              'hover:bg-accent hover:text-accent-foreground',
              'focus:outline-none focus:ring-1 focus:ring-ring',
              disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            {meridiem}
          </button>
        ) : null}
      </div>
    </div>
  )
}

TimeInput.displayName = 'TimeInput'

export { TimeInput }
